import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { myNav } from '@/components/px/nav'
import {
  deleteMessage,
  editMessage,
  fetchMessages,
  fetchMoreMessages,
  messagesRetryClicked,
  postMessage
} from '@/features/messages/messagesSlice'
import { fetchUsers } from '@/features/users/usersSlice'
import { selectSessionUserIdHex } from '@/features/users/usersSelectors'
import type { ApiMessage } from '@/features/messages/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { formatRelativeTime, initialsFor } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * Messages (`/messages`, PX-W6.2): the org thread — list, post, edit own,
 * delete own, load more. The thread is tagged with the `{ orgId, <org> }`
 * context tuple the messages endpoints REQUIRE (blank context params are a
 * 400 on list; an empty `contextIds` array is a 400 on create — the opposite
 * of the activity contract); the slice stamps it, this page only supplies
 * content. "Own" is decided by matching the row's hex `userId` against the
 * session user's `idHex` from the directory — a server projection, not a
 * client-side derivation.
 */
export default function MessagesPage() {
  const { t } = useTranslation(['messages', 'people'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const items = useAppSelector((s) => s.messages.items)
  const nextCursor = useAppSelector((s) => s.messages.nextCursor)
  const cache = useAppSelector((s) => s.messages.cache)
  const error = useAppSelector((s) => s.messages.error)
  const mutationError = useAppSelector((s) => s.messages.mutationError)
  const sessionUserId = useAppSelector((s) => s.session.user?.id ?? null)
  const sessionUserHex = useAppSelector((s) => selectSessionUserIdHex(s, sessionUserId))
  const usersById = useAppSelector((s) => s.users.byId)

  const [draft, setDraft] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')

  useEffect(() => {
    // The thread plus the directory (author names + the session hex mapping).
    void dispatch(fetchMessages({ limit: 50 }))
    void dispatch(fetchUsers())
  }, [dispatch])

  const authorName = (row: ApiMessage): string => {
    if (row.userId === undefined) return t('messages:unknownAuthor')
    const match = Object.values(usersById).find((user) => user.idHex === row.userId)
    return match?.name ?? t('messages:unknownAuthor')
  }

  const onDraftChanged = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setDraft(event.target.value)
    setValidation(null)
  }

  const onSendClicked = (): void => {
    if (draft.trim() === '') {
      setValidation('messages:validation.contentRequired')
      return
    }
    void dispatch(postMessage({ content: draft.trim() }))
    setDraft('')
  }

  const onEditStarted = (row: ApiMessage): void => {
    if (row.id === undefined) return
    setEditingId(row.id)
    setEditDraft(row.content ?? '')
  }

  const onEditSaved = (id: string): void => {
    if (editDraft.trim() === '') return
    void dispatch(editMessage({ id, content: editDraft.trim() }))
    setEditingId(null)
    setEditDraft('')
  }

  const onDeleteClicked = (row: ApiMessage): void => {
    if (row.id === undefined) return
    if (!window.confirm(t('messages:delete.confirm'))) return
    void dispatch(deleteMessage(row.id))
  }

  const onLoadMoreClicked = (): void => {
    void dispatch(fetchMoreMessages())
  }

  const own = (row: ApiMessage): boolean =>
    sessionUserHex !== null && row.userId === sessionUserHex

  return (
    <AppShell
      moduleLabel={t('people:title')}
      breadcrumb={<span>{t('messages:title')}</span>}
      contextPill={t('people:chrome.contextPill')}
      nav={myNav('consultant')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('messages:title')}</h1>
        <p className="text-sm text-muted-foreground">{t('messages:lede')}</p>
      </header>

      <section className="mt-4 rounded-lg border border-border bg-card">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <div className="p-4">
            <ForbiddenPanel requested="/{orgId}/messages" />
          </div>
        ) : error !== null ? (
          <div className="p-4">
            <ErrorPanel
              error={error}
              onRetryClicked={() => {
                dispatch(messagesRetryClicked())
                void dispatch(fetchMessages({ limit: 50 }))
              }}
            />
          </div>
        ) : items.length === 0 ? (
          <EmptyPanel body={t('messages:empty')} />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((row, index) => {
              const id = row.id ?? `index-${index}`
              const isEditing = editingId === row.id
              return (
                <li key={id} className="px-4 py-3">
                  <div className="flex items-start gap-2.5">
                    <span
                      aria-hidden
                      className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-people-bg text-xs font-semibold text-people-ink"
                    >
                      {initialsFor(authorName(row))}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-medium text-foreground">
                          {authorName(row)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(row.createdAt) ?? ''}
                        </span>
                      </div>
                      {isEditing ? (
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <textarea
                            rows={2}
                            value={editDraft}
                            onChange={(event) => setEditDraft(event.target.value)}
                            className="w-full max-w-xl rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground"
                          />
                          <button
                            type="button"
                            onClick={() => row.id !== undefined && onEditSaved(row.id)}
                            className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                          >
                            {t('messages:edit.save')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="rounded-md border border-input px-2.5 py-1 text-xs text-ink-soft hover:bg-muted"
                          >
                            {t('messages:edit.cancel')}
                          </button>
                        </div>
                      ) : (
                        <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink-soft">
                          {row.content ?? ''}
                        </p>
                      )}
                    </div>
                    {own(row) && row.id !== undefined && !isEditing && (
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => onEditStarted(row)}
                          className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted"
                        >
                          {t('messages:edit.action')}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteClicked(row)}
                          className="rounded-md border border-input px-2 py-1 text-xs text-blocked hover:bg-muted"
                        >
                          {t('messages:delete.action')}
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {nextCursor !== null && (
        <div className="mt-4">
          <button
            type="button"
            onClick={onLoadMoreClicked}
            className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
          >
            {t('messages:loadMore')}
          </button>
        </div>
      )}

      {mutationError !== null && (
        <p role="alert" className="mt-3 text-sm text-blocked">
          {toMessage(mutationError)}
        </p>
      )}

      <section className="mt-4 rounded-lg border border-border bg-card px-4 py-3">
        <label htmlFor="message-composer" className="text-sm text-ink-soft">
          {t('messages:composer.label')}
        </label>
        <textarea
          id="message-composer"
          rows={3}
          value={draft}
          onChange={onDraftChanged}
          placeholder={t('messages:composer.placeholder')}
          className="mt-1.5 w-full rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground"
        />
        {validation !== null && (
          <p role="alert" className="mt-1 text-sm text-blocked">
            {t(validation)}
          </p>
        )}
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={onSendClicked}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t('messages:composer.send')}
          </button>
        </div>
      </section>
    </AppShell>
  )
}
